import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Bed, Bath, Minus, Plus, Download, Printer, Sparkles, Loader2, Send, RotateCcw, RefreshCw, MessageCircle, Car, Layers, MapPin, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { getUserProfile } from '../utils/userProfile';
import useGooglePlacesAutocomplete from '../hooks/useGooglePlaces';
import InteractiveMap from '../components/InteractiveMap';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Footer from '../components/sections/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GMAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const PREFS = [
  { id: 'open_kitchen', en: 'Open Kitchen', zh: '开放式厨房' },
  { id: 'large_living', en: 'Large Living Room', zh: '加大客厅' },
  { id: 'laundry', en: 'Separate Laundry', zh: '独立洗衣房' },
  { id: 'dual_master', en: 'Dual Master Suite', zh: '双主卧套房' },
  { id: 'walk_in_closet', en: 'Walk-in Closet', zh: '步入式衣帽间' },
  { id: 'office', en: 'Home Office', zh: '书房/办公室' },
  { id: 'pantry', en: 'Pantry', zh: '储物间' },
  { id: 'island_kitchen', en: 'Kitchen Island', zh: '厨房中岛' },
];

const DesignMyHome = () => {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const navigate = useNavigate();
  const addressRef = useRef(null);
  const printRef = useRef(null);
  const chatEndRef = useRef(null);
  const mapDivRef = useRef(null);

  // Login gate + prefill contact info
  const loggedAccount = localStorage.getItem('admin_email') || '';
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      toast.error(isZh ? '请登录系统' : 'Please login first');
      navigate('/profile', { state: { returnTo: '/design' } });
      return;
    }
    // Prefill from login account
    if (loggedAccount.includes('@')) setContactEmail(loggedAccount);
    else setContactPhone(loggedAccount);
    // Also try loading profile for full info
    const loadProfile = async () => {
      try {
        const r = await axios.get(`${API}/admin/user-profile/${encodeURIComponent(loggedAccount)}`);
        if (r.data?.email) setContactEmail(r.data.email);
        if (r.data?.phone) setContactPhone(r.data.phone);
      } catch {}
    };
    loadProfile();
  }, []);

  const [showResult, setShowResult] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [planHistory, setPlanHistory] = useState([]);
  const [address, setAddress] = useState('');
  const [latLng, setLatLng] = useState(null);
  const [mapUrl, setMapUrl] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(() => `design-${Date.now()}`);
  const [allProducts, setAllProducts] = useState([]);
  const [matchedProducts, setMatchedProducts] = useState([]);
  const [matchIdx, setMatchIdx] = useState(0);
  const [showMapOverlay, setShowMapOverlay] = useState(false);
  const [overlayPos, setOverlayPos] = useState({ x: 200, y: 100 });
  const [overlayRotation, setOverlayRotation] = useState(0);
  const [overlayScale] = useState(0.45);
  const [savedComposite, setSavedComposite] = useState(null);
  const overlayMapRef = useRef(null);
  const overlayGmapRef = useRef(null);
  const overlayGmapDivRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const [params, setParams] = useState({
    building_type: 'adu', square_footage: 800, bedrooms: 2, bathrooms: 1,
    stories: 1, garage: 'none', preferences: []
  });

  // Load all products for matching
  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/products`);
        setAllProducts(r.data.products || []);
      } catch {}
      // Prefill address from profile
      const prof = await getUserProfile();
      if (prof?.address && !address) setAddress(prof.address);
    };
    load();
  }, []);

  useGooglePlacesAutocomplete(addressRef, {
    componentRestrictions: { country: 'us' },
    onPlaceSelect: (data) => {
      setAddress(data.formatted_address || data.street_address);
      setLatLng(data.latitude && data.longitude ? { lat: data.latitude, lng: data.longitude } : null);
    }
  });

  // Update map when address changes
  useEffect(() => {
    if (address && GMAP_KEY) {
      setMapUrl(`https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(address)}&zoom=20&size=800x500&maptype=satellite&markers=color:red%7C${encodeURIComponent(address)}&key=${GMAP_KEY}`);
    }
  }, [address]);

  // Interactive map
  useEffect(() => {
    if (!latLng || !mapDivRef.current || !window.google?.maps || showResult) return;
    const map = new window.google.maps.Map(mapDivRef.current, {
      center: latLng, zoom: 20, mapTypeId: 'satellite',
      mapTypeControl: false, streetViewControl: false, fullscreenControl: true, zoomControl: true
    });
    new window.google.maps.Marker({ position: latLng, map, title: address });
  }, [latLng, address, showResult]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  // Initialize overlay interactive map when modal opens — use same address/latLng as input
  useEffect(() => {
    if (!showMapOverlay || !overlayGmapDivRef.current || !window.google?.maps) return;

    const initMap = (center) => {
      const map = new window.google.maps.Map(overlayGmapDivRef.current, {
        center, zoom: 20, mapTypeId: 'satellite',
        zoomControl: false, scrollwheel: true, gestureHandling: 'greedy',
        fullscreenControl: false, streetViewControl: false, mapTypeControl: true,
        rotateControl: false, scaleControl: false, panControl: false,
      });
      new window.google.maps.Marker({ position: center, map, title: address || '' });
      overlayGmapRef.current = map;
    };

    if (latLng) {
      initMap(latLng);
    } else if (address) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results[0]) {
          initMap(results[0].geometry.location);
        }
      });
    }
  }, [showMapOverlay, latLng, address]);

  const togglePref = (id) => setParams(p => ({ ...p, preferences: p.preferences.includes(id) ? p.preferences.filter(x => x !== id) : [...p.preferences, id] }));
  const savePlan = (p) => { if (p) setPlanHistory(h => [...h, JSON.parse(JSON.stringify(p))]); };

  // Strict match products from DB: bedrooms + bathrooms + garage + stories must match, sqft within 100
  const matchProducts = () => {
    const designBeds = params.bedrooms;
    const designBaths = params.bathrooms;
    const designSqft = params.square_footage;
    const designGarage = (params.garage || 'none').toLowerCase();
    const designStories = params.stories || 1;

    // Only products with plan_image
    const withPlan = allProducts.filter(p => p.plan_image);

    let candidates = withPlan.filter(p => {
      if (p.bedrooms !== designBeds) return false;
      if (p.bathrooms !== designBaths) return false;
      // Garage: normalize — 'None', 'none', '', null all = no garage
      const pGarage = (p.garage || '').toLowerCase();
      const pIsNone = !pGarage || pGarage === 'none' || pGarage === 'null';
      const dIsNone = designGarage === 'none';
      if (pIsNone !== dIsNone) {
        if (!pIsNone && !dIsNone && pGarage !== designGarage) return false;
        if (pIsNone !== dIsNone) return false;
      }
      // Stories: DB may store '1F', '2F' or 1, 2
      const pStories = typeof p.stories === 'string' ? parseInt(p.stories) || 1 : (p.stories || 1);
      if (pStories !== designStories) return false;
      // Sqft within 100
      if (Math.abs((p.sqft || 0) - designSqft) > 100) return false;
      return true;
    });

    candidates.sort((a, b) => Math.abs(a.sqft - designSqft) - Math.abs(b.sqft - designSqft));
    return candidates;
  };

  // Map overlay drag handlers
  const draggingRef = useRef(false);
  const handleOverlayMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    draggingRef.current = true;
    setDragging(true);
    dragStart.current = { x: e.clientX - overlayPos.x, y: e.clientY - overlayPos.y };
  };
  const handleOverlayMouseMove = (e) => {
    if (!draggingRef.current) return;
    setOverlayPos({ x: e.clientX - dragStart.current.x, y: e.clientY - dragStart.current.y });
  };
  const handleOverlayMouseUp = () => { draggingRef.current = false; setDragging(false); };

  // Touch handlers for mobile
  const handleOverlayTouchStart = (e) => {
    const t = e.touches[0];
    setDragging(true);
    dragStart.current = { x: t.clientX - overlayPos.x, y: t.clientY - overlayPos.y };
  };
  const handleOverlayTouchMove = (e) => {
    if (!dragging) return;
    const t = e.touches[0];
    setOverlayPos({ x: t.clientX - dragStart.current.x, y: t.clientY - dragStart.current.y });
  };

  // Save composite screenshot
  const saveComposite = async () => {
    if (!overlayMapRef.current) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      // Get current map center/zoom for static background
      const gmap = overlayGmapRef.current;
      if (gmap) {
        const c = gmap.getCenter();
        const z = gmap.getZoom();
        const staticUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${c.lat()},${c.lng()}&zoom=${z}&size=1074x840&maptype=satellite&key=${GMAP_KEY}`;
        // Load static image into overlay background for html2canvas capture
        const bgImg = overlayMapRef.current.querySelector('.overlay-map-bg');
        if (bgImg) {
          bgImg.src = staticUrl;
          await new Promise(r => { bgImg.onload = r; setTimeout(r, 2000); });
        }
      }
      const el = overlayMapRef.current;
      const rect = el.getBoundingClientRect();
      const canvas = await html2canvas(el, {
        useCORS: true, allowTaint: true,
        scale: 2,
        width: rect.width,
        height: rect.height,
      });
      const dataUrl = canvas.toDataURL('image/png');
      setSavedComposite(dataUrl);
      setShowMapOverlay(false);
      toast.success(isZh ? '方案图已保存' : 'Plan saved to map');
    } catch { toast.error('Failed to save'); }
  };



  const handleGenerate = async () => {
    if (!address.trim()) { toast.error(isZh ? '请输入地址' : 'Please enter an address'); return; }

    setGenerating(true);
    try {
      const res = await axios.post(`${API}/design/generate`, { address, ...params });
      if (res.data.status === 'ok') {
        savePlan(plan);
        setPlan(res.data.plan);
        // Try matching products from DB
        const matched = matchProducts();
        setMatchedProducts(matched);
        setMatchIdx(0);
        setShowResult(true);
        setChatMessages([{ role: 'system', text: isZh ? '平面方案已生成！在下方对话框中输入修改建议。' : 'Floor plan generated! Type modifications in the chat below.' }]);
        toast.success(isZh ? '平面方案已生成！' : 'Floor plan generated!');
        // Log generation
        const loggedEmail = localStorage.getItem('admin_email') || '';
        try {
          await axios.post(`${API}/admin/design-logs`, {
            email: contactEmail || (loggedEmail.includes('@') ? loggedEmail : ''),
            phone: contactPhone || (!loggedEmail.includes('@') ? loggedEmail : ''),
            building_type: params.building_type,
            stories: `${params.stories}F`,
            garage: params.garage,
            preferences: params.preferences,
            address,
            sqft: params.square_footage,
            bedrooms: params.bedrooms,
            bathrooms: params.bathrooms,
            plan_image: matched[0]?.plan_image || '',
          });
        } catch {}
      } else { toast.error(res.data.message || 'Failed'); }
    } catch { toast.error(isZh ? '生成失败' : 'Generation failed'); }
    finally { setGenerating(false); }
  };

  const handleUndo = () => {
    if (!planHistory.length) return;
    setPlan(planHistory[planHistory.length - 1]);
    setPlanHistory(h => h.slice(0, -1));
    toast.success(isZh ? '已撤销' : 'Undone');
  };

  const handleChatSend = async () => {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMessages(p => [...p, { role: 'user', text: msg }]);
    setChatLoading(true);
    try {
      const res = await axios.post(`${API}/design/chat`, { session_id: sessionId, message: msg, current_plan: JSON.stringify(plan) });
      if (res.data.status === 'ok') {
        const resp = res.data.response;
        try {
          const clean = resp.replace(/```json?\n?/g, '').replace(/```/g, '').trim();
          const updated = JSON.parse(clean);
          if (updated.rooms) { savePlan(plan); setPlan(updated); setChatMessages(p => [...p, { role: 'assistant', text: isZh ? '已更新平面方案。' : 'Plan updated.' }]); }
          else { setChatMessages(p => [...p, { role: 'assistant', text: resp }]); }
        } catch { setChatMessages(p => [...p, { role: 'assistant', text: resp }]); }
      }
    } catch { setChatMessages(p => [...p, { role: 'assistant', text: isZh ? '处理失败' : 'Failed' }]); }
    finally { setChatLoading(false); }
  };

  const handlePdf = async () => {
    if (!printRef.current) return;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const { default: jsPDF } = await import('jspdf');
      
      const canvas = await html2canvas(printRef.current, { scale: 2, useCORS: true, backgroundColor: '#fff' });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();

      // Logo header only
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        await new Promise((res, rej) => { logoImg.onload = res; logoImg.onerror = rej; logoImg.src = '/api/uploads/8bb5e435c484.png'; });
        const lc = document.createElement('canvas'); lc.width = logoImg.width; lc.height = logoImg.height; lc.getContext('2d').drawImage(logoImg, 0, 0);
        pdf.addImage(lc.toDataURL('image/png'), 'PNG', 10, 5, 50, 18);
      } catch {}
      pdf.setDrawColor(220, 38, 38);
      pdf.setLineWidth(0.5);
      pdf.line(10, 25, pw - 10, 25);

      // Content — no pagination, fit to page
      const contentW = pw - 20;
      const r = contentW / canvas.width;
      const contentH = canvas.height * r;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 28, contentW, contentH);

      // Plan on Site Map — new page if exists
      if (savedComposite) {
        pdf.addPage();
        try {
          const logoImg2 = new Image();
          logoImg2.crossOrigin = 'anonymous';
          await new Promise((res, rej) => { logoImg2.onload = res; logoImg2.onerror = rej; logoImg2.src = '/api/uploads/8bb5e435c484.png'; });
          const lc2 = document.createElement('canvas'); lc2.width = logoImg2.width; lc2.height = logoImg2.height; lc2.getContext('2d').drawImage(logoImg2, 0, 0);
          pdf.addImage(lc2.toDataURL('image/png'), 'PNG', 10, 5, 50, 18);
        } catch {}
        pdf.line(10, 25, pw - 10, 25);
        pdf.setFontSize(14);
        pdf.setTextColor(0);
        pdf.text('Plan on Site Map', 14, 35);
        const mapW = pw - 28;
        const mapH = mapW * (420 / 537);
        pdf.addImage(savedComposite, 'PNG', 14, 42, mapW, mapH);
      }

      pdf.save(`ALIGCUS_Design_${address.split(',')[0].replace(/\s+/g, '_').substring(0, 25)}.pdf`);
    } catch { toast.error('PDF failed'); }
  };

  // Bold, saturated room colors
  const getRoomColor = (name) => {
    const n = (name || '').toLowerCase();
    if (n.includes('bedroom') || n.includes('master')) return '#e878a8';
    if (n.includes('bath')) return '#58c8c8';
    if (n.includes('kitchen')) return '#f0b830';
    if (n.includes('living')) return '#68a8e8';
    if (n.includes('dining')) return '#d8b060';
    if (n.includes('garage')) return '#909090';
    if (n.includes('laundry')) return '#88a8d0';
    if (n.includes('entry') || n.includes('foyer') || n.includes('hall')) return '#b0b0b0';
    if (n.includes('closet') || n.includes('storage')) return '#a0a0a0';
    if (n.includes('office') || n.includes('study')) return '#7898d0';
    if (n.includes('pantry')) return '#c8b070';
    return '#98c098';
  };

  const renderFloorPlan = () => {
    if (!plan?.rooms) return null;
    const sc = 18;
    let maxX = 0, maxY = 0;
    plan.rooms.forEach(r => { maxX = Math.max(maxX, (r.x || 0) + (r.width || 0)); maxY = Math.max(maxY, (r.y || 0) + (r.height || 0)); });
    const W = Math.max(maxX, plan.width_ft || 40) * sc;
    const H = Math.max(maxY, plan.height_ft || 30) * sc;
    return (
      <div className="relative border-2 border-gray-800 rounded-lg overflow-hidden" style={{ width: '100%', paddingBottom: `${(H / W) * 100}%`, backgroundColor: '#ffffff' }}>
        {plan.rooms.map((r, i) => {
          const left = ((r.x || 0) * sc / W) * 100;
          const top = ((r.y || 0) * sc / H) * 100;
          const width = ((r.width || 0) * sc / W) * 100;
          const height = ((r.height || 0) * sc / H) * 100;
          return (
            <div key={i} className="absolute border border-gray-800 flex flex-col items-center justify-center overflow-hidden"
              style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%`, backgroundColor: getRoomColor(r.name) }}>
              <span className="text-xs font-black text-gray-900 leading-tight text-center px-0.5" style={{ fontSize: 'clamp(7px, 1.2vw, 13px)' }}>{r.name}</span>
              <span className="text-gray-700 font-bold leading-tight" style={{ fontSize: 'clamp(6px, 0.9vw, 10px)' }}>{r.width}'x{r.height}' ({r.sqft}sf)</span>
            </div>
          );
        })}
      </div>
    );
  };

  // ===== DESIGN FORM (address + map + params) =====
  if (!showResult) return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gray-900 text-white py-10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-2">{isZh ? '我的房屋设计' : 'Design MyHome'}</h1>
          <p className="text-gray-400">{isZh ? '输入地址，选择参数，AI 为您生成定制平面方案' : 'Enter address, set parameters, let AI generate your custom floor plan'}</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Address + Map */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-bold text-gray-700 mb-2 block">{isZh ? '项目地址' : 'Project Address'}</label>
              <input ref={addressRef} type="text" value={address} onChange={e => setAddress(e.target.value)}
                placeholder={isZh ? '开始输入地址...' : 'Start typing address...'}
                className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:border-red-600 outline-none" data-testid="design-address" />
            </div>
            {/* Map */}
            {latLng ? (
              <div ref={mapDivRef} className="w-full rounded-xl overflow-hidden shadow-lg" style={{ height: '420px' }} data-testid="design-map" />
            ) : address ? (
              <InteractiveMap address={address} height="420px" zoom={20} />
            ) : (
              <div className="w-full bg-gray-200 rounded-xl flex items-center justify-center" style={{ height: '420px' }}>
                <p className="text-gray-500">{isZh ? '输入地址后显示卫星视图' : 'Satellite view after entering address'}</p>
              </div>
            )}
            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">Email</label>
                <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)}
                  placeholder="Email" className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-red-600 outline-none" data-testid="design-email" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase">{isZh ? '电话' : 'Phone'}</label>
                <input type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)}
                  placeholder={isZh ? '电话号码' : 'Phone number'} className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-red-600 outline-none" data-testid="design-phone" />
              </div>
            </div>
          </div>

          {/* Right: Parameters */}
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-900">{isZh ? '设计参数' : 'Design Parameters'}</h2>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">{isZh ? '建筑类型' : 'Type'}</label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {[{ v:'adu',l:'ADU' },{ v:'jadu',l:'JADU' },{ v:'addition',l:isZh?'扩建':'Addition' }].map(o=>(
                  <button key={o.v} onClick={()=>setParams(p=>({...p,building_type:o.v}))} className={`py-2 rounded-lg text-sm font-medium border-2 ${params.building_type===o.v?'border-red-600 bg-red-50 text-red-600':'border-gray-200'}`}>{o.l}</button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">{isZh?'面积':'Area'}: <span className="text-red-600">{params.square_footage} sf</span></label>
              <input type="range" min={400} max={1200} step={50} value={params.square_footage} onChange={e=>setParams(p=>({...p,square_footage:+e.target.value}))} className="w-full mt-1.5 accent-red-600" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[{k:'bedrooms',icon:Bed,l:isZh?'卧室':'Beds',mn:0,mx:4},{k:'bathrooms',icon:Bath,l:isZh?'卫浴':'Baths',mn:1,mx:3}].map(f=>(
                <div key={f.k}>
                  <label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1"><f.icon className="w-3 h-3"/>{f.l}</label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <button onClick={()=>setParams(p=>({...p,[f.k]:Math.max(f.mn,p[f.k]-1)}))} className="w-7 h-7 rounded-full border flex items-center justify-center"><Minus className="w-3 h-3"/></button>
                    <span className="text-lg font-bold w-5 text-center">{params[f.k]}</span>
                    <button onClick={()=>setParams(p=>({...p,[f.k]:Math.min(f.mx,p[f.k]+1)}))} className="w-7 h-7 rounded-full border flex items-center justify-center"><Plus className="w-3 h-3"/></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1"><Layers className="w-3 h-3"/>{isZh?'层数':'Stories'}</label>
                <div className="flex gap-2 mt-1.5">{[1,2].map(n=><button key={n} onClick={()=>setParams(p=>({...p,stories:n}))} className={`flex-1 py-1.5 rounded-lg text-sm font-medium border-2 ${params.stories===n?'border-red-600 bg-red-50 text-red-600':'border-gray-200'}`}>{n}{isZh?'层':'F'}</button>)}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1"><Car className="w-3 h-3"/>{isZh?'车库':'Garage'}</label>
                <select value={params.garage} onChange={e=>setParams(p=>({...p,garage:e.target.value}))} className="w-full mt-1.5 py-1.5 px-3 border-2 border-gray-200 rounded-lg text-sm focus:border-red-600 outline-none">
                  <option value="none">{isZh?'无':'None'}</option><option value="attached">{isZh?'连体':'Attached'}</option><option value="detached">{isZh?'独立':'Detached'}</option>
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase">{isZh?'偏好':'Preferences'}</label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {PREFS.map(p=><button key={p.id} onClick={()=>togglePref(p.id)} className={`px-2.5 py-1 rounded-full text-xs font-medium border ${params.preferences.includes(p.id)?'bg-red-600 text-white border-red-600':'bg-white text-gray-600 border-gray-200'}`}>{isZh?p.zh:p.en}</button>)}
              </div>
            </div>

            <Button onClick={handleGenerate} disabled={generating} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 text-base" data-testid="generate-design-btn">
              {generating?<><Loader2 className="w-5 h-5 mr-2 animate-spin"/>{isZh?'AI 生成中...':'Generating...'}</>:<><Sparkles className="w-5 h-5 mr-2"/>{isZh?'AI 生成平面方案':'Generate Floor Plan'}</>}
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );

  // ===== RESULT VIEW (Plan + Chat) =====
  return (
    <div className="min-h-screen bg-gray-50">
      <section className="bg-gray-900 text-white py-6">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold">{isZh?'平面设计方案':'Floor Plan Design'}</h1>
          <Button onClick={()=>{setShowResult(false);}} variant="outline" className="border-white/30 text-white hover:bg-white/10 text-sm">{isZh?'返回修改参数':'Back to Edit'}</Button>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-6" data-no-zoom>
          {/* Plan Panel */}
          <div>
            <div className="flex gap-2 mb-4 flex-wrap">
              <Button onClick={()=>{ if (matchedProducts.length > 1) { setMatchIdx(prev => (prev + 1) % matchedProducts.length); toast.success(isZh ? '已切换备选方案' : 'Switched to alternate plan'); } else { savePlan(plan); handleGenerate(); } }} disabled={generating} variant="outline" className="border-gray-300 text-sm">
                <RefreshCw className={`w-4 h-4 mr-1 ${generating?'animate-spin':''}`}/>{isZh?'重新生成':'Regenerate'}{matchedProducts.length > 1 ? ` (${matchIdx+1}/${matchedProducts.length})` : ''}
              </Button>
              <Button onClick={handleUndo} variant="outline" className="border-gray-300 text-sm" disabled={!planHistory.length}>
                <RotateCcw className="w-4 h-4 mr-1"/>{isZh?'撤销':'Undo'}{planHistory.length>0&&` (${planHistory.length})`}
              </Button>
              <Button onClick={handlePdf} className="bg-red-600 hover:bg-red-700 text-white text-sm"><Download className="w-4 h-4 mr-1"/>PDF</Button>
              <Button onClick={()=>window.print()} variant="outline" className="border-gray-300 text-sm"><Printer className="w-4 h-4 mr-1"/>{isZh?'打印':'Print'}</Button>
              {(matchedProducts[matchIdx]?.plan_image || plan) && (
                <Button onClick={() => setShowMapOverlay(true)} className="bg-gray-900 hover:bg-gray-800 text-white text-sm"><MapPin className="w-4 h-4 mr-1"/>{isZh?'放置到地图':'Place on Map'}</Button>
              )}
            </div>

            <div ref={printRef} className="bg-white rounded-xl shadow-lg p-6">
              <p className="text-xs text-gray-400 uppercase">{isZh?'项目地址':'Address'}</p>
              <h2 className="text-lg font-bold text-gray-900 mb-3">{address}</h2>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {[{l:isZh?'面积':'Area',v:`${plan.total_sqft}sf`},{l:isZh?'户型':'Layout',v:`${params.bedrooms}B/${params.bathrooms}BA`},{l:isZh?'层数':'Stories',v:`${params.stories}F`},{l:isZh?'车库':'Garage',v:params.garage==='none'?'-':params.garage}].map((x,i)=>(
                  <div key={i} className="bg-red-600 text-white rounded-lg p-2 text-center"><p className="text-xs opacity-80">{x.l}</p><p className="text-sm font-bold">{x.v}</p></div>
                ))}
              </div>
              {/* Floor Plan — show matched product plan_image if available, else AI-generated */}
              {matchedProducts[matchIdx]?.plan_image ? (
                <div className="rounded-lg overflow-hidden border-2 border-gray-300">
                  <img src={matchedProducts[matchIdx].plan_image} alt="Floor Plan" className="w-full object-contain" />
                  <div className="bg-gray-50 p-2 text-center text-xs text-gray-500">
                    {matchedProducts[matchIdx].title_en || matchedProducts[matchIdx].title_zh} — {matchedProducts[matchIdx].bedrooms}B/{matchedProducts[matchIdx].bathrooms}BA · {matchedProducts[matchIdx].sqft}sf
                  </div>
                </div>
              ) : (
                renderFloorPlan()
              )}
              <h3 className="text-xs font-bold text-gray-700 uppercase mt-4 mb-2">{isZh?'房间清单':'Rooms'}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                {plan.rooms?.map((r,i)=><div key={i} className="flex items-center gap-2 p-2 rounded border border-gray-100 text-xs"><div className="w-3 h-3 rounded" style={{backgroundColor:getRoomColor(r.name)}}/><span className="font-semibold">{r.name}</span><span className="text-gray-400 ml-auto">{r.sqft}sf</span></div>)}
              </div>
              {plan.description&&<div className="bg-gray-50 rounded-lg p-3 mt-3 text-xs"><p className="font-bold text-gray-700 mb-1">{isZh?'布局说明':'Notes'}</p><p className="text-gray-600">{isZh?(plan.description_zh||plan.description):plan.description}</p></div>}
              <p className="text-xs text-gray-400 italic mt-3">{isZh?'* AI 示意图，非施工图。':'* AI conceptual layout, not construction drawings.'}</p>
              <div className="mt-3 pt-3 border-t flex justify-between text-xs text-gray-500"><div><p className="font-semibold text-gray-900">ALIGCUS</p><p>www.aligcus.com</p></div><div className="text-right"><p>626-803-8000</p><p>408-900-6688</p></div></div>
            </div>

            {/* Saved Composite — Plan on Map */}
            {savedComposite && (
              <div className="mt-6 bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{isZh ? '方案定位图' : 'Plan on Site Map'}</h3>
                <img src={savedComposite} alt="Plan on Map" className="rounded-lg border" style={{ width: '537px', height: '420px' }} />
              </div>
            )}
          </div>
      </div>

      {/* Map Overlay Modal */}
      {showMapOverlay && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-no-zoom
          onMouseMove={handleOverlayMouseMove} onMouseUp={handleOverlayMouseUp} onTouchMove={handleOverlayTouchMove} onTouchEnd={() => { draggingRef.current = false; setDragging(false); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl" onClick={e => e.stopPropagation()}>
            <div className="bg-gray-900 text-white px-6 py-3 flex items-center justify-between rounded-t-2xl">
              <h3 className="font-bold">{isZh ? '拖拽方案图到地图上' : 'Drag Floor Plan onto Map'}</h3>
              <div className="flex gap-2">
                <Button onClick={saveComposite} className="bg-red-600 hover:bg-red-700 text-white text-sm">{isZh ? '保存' : 'Save'}</Button>
                <Button onClick={() => setShowMapOverlay(false)} variant="outline" className="border-white/30 text-white hover:bg-white/10 text-sm">{isZh ? '取消' : 'Cancel'}</Button>
              </div>
            </div>
            {/* Controls */}
            <div className="px-6 py-2 bg-gray-50 flex items-center gap-4 text-sm border-b">
              <label className="flex items-center gap-2"><RotateCw className="w-4 h-4" />{isZh ? '旋转' : 'Rotate'}:
                <input type="range" min={0} max={360} value={overlayRotation} onChange={e => setOverlayRotation(+e.target.value)} className="w-32 accent-red-600" />
                <span className="text-gray-500 w-10">{overlayRotation}°</span>
              </label>
            </div>
            {/* Map + Draggable Plan */}
            <div ref={overlayMapRef} className="relative rounded-b-2xl" style={{ width: '100%', height: '500px', cursor: dragging ? 'grabbing' : 'default', overflow: 'hidden' }}>
              {/* Interactive Map Background */}
              <div ref={overlayGmapDivRef} style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 }} />
              {/* Hidden static image for html2canvas capture */}
              <img src="" alt="" className="overlay-map-bg absolute inset-0 w-full h-full object-cover" style={{ zIndex: 1, opacity: 0, pointerEvents: 'none' }} />
              {/* Draggable Plan Overlay */}
              {(matchedProducts[matchIdx]?.plan_image || plan) && (
                <div
                  className="absolute cursor-grab active:cursor-grabbing"
                  style={{
                    left: `${overlayPos.x}px`, top: `${overlayPos.y}px`,
                    transform: `rotate(${overlayRotation}deg) scale(${overlayScale})`,
                    transformOrigin: 'center center',
                    opacity: 0.5,
                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))',
                    touchAction: 'none',
                    zIndex: 10,
                  }}
                  onMouseDown={handleOverlayMouseDown}
                  onTouchStart={handleOverlayTouchStart}
                >
                  {matchedProducts[matchIdx]?.plan_image ? (
                    <img src={matchedProducts[matchIdx].plan_image} alt="Plan" style={{ maxWidth: '400px', pointerEvents: 'none' }} />
                  ) : (
                    <div className="bg-white/80 rounded-lg p-4 border-2 border-red-600" style={{ width: '300px' }}>
                      {renderFloorPlan()}
                    </div>
                  )}
                </div>
              )}
              {/* Hint */}
              <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs px-3 py-1.5 rounded-lg" style={{ zIndex: 20 }}>
                {isZh ? '拖拽方案图移动位置，使用滑块旋转，可放大缩小地图' : 'Drag to move plan, use slider to rotate, zoom map freely'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesignMyHome;
